import { DatePipe } from '@angular/common';
import {
  Component,
  forwardRef,
  inject,
  input,
  OnChanges,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  AMENDMENT_DIALOG_CONFIG,
  AmendmentDialog,
  AmendmentDialogData,
} from '@app/features/amendments/amendment-dialog/amendment-dialog';
import { Amendment, AmendmentDocument } from '@app/features/amendments/amendment.model';
import { AmendmentService } from '@app/features/amendments/amendment.service';
import { ButtonEstablishAmendment } from '@app/features/amendments/button-establish-amendment/button-establish-amendment';
import { ContractStatusComponent } from '@app/features/contracts/contract-status/contract-status';
import { Contract } from '@app/features/contracts/contract.model';
import { DocumentService } from '@app/features/documents/document-service';
import { DocumentTagLabelPipe } from '@app/features/documents/document-tag-label-pipe';
import { RxjsDirective } from '@app/shared/rxjs.directive';

/**
 * Les avenants d'un contrat : leur numéro, leurs motifs, leur date d'effet, leur circuit et leurs
 * pièces. Un avenant signé reste attaché au contrat et se consulte avec lui.
 */
@Component({
  selector: 'app-contract-amendments',
  imports: [
    DatePipe,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    // Le bouton ouvre le dialogue de l'avenant, qui montre le formulaire du contrat, qui montre
    // cet onglet : la référence attend.
    forwardRef(() => ButtonEstablishAmendment),
    ContractStatusComponent,
    DocumentTagLabelPipe,
  ],
  templateUrl: './contract-amendments.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./contract-amendments.scss'],
})
export class ContractAmendments extends RxjsDirective implements OnChanges {
  readonly contract = input.required<Contract>();

  private readonly amendmentService = inject(AmendmentService);
  private readonly dialog = inject(MatDialog);
  private readonly documentService = inject(DocumentService);

  readonly amendments = signal<Amendment[]>([]);

  constructor() {
    super();
    this.register(
      this.amendmentService.updated$.subscribe((updated) => {
        if (updated.contractId === this.contract().id) {
          this.load();
        }
      }),
    );
  }

  ngOnChanges(): void {
    this.load();
  }

  load(): void {
    const contractId = this.contract().id;
    if (!contractId) {
      return;
    }
    this.register(
      this.amendmentService
        .listOfContract(contractId)
        .subscribe((amendments) => this.amendments.set(amendments)),
    );
  }

  open(amendment: Amendment): void {
    const dialogRef = this.dialog.open<AmendmentDialog, AmendmentDialogData>(AmendmentDialog, {
      data: { contract: this.contract(), amendment },
      ...AMENDMENT_DIALOG_CONFIG,
    });
    this.register(dialogRef.afterClosed().subscribe(() => this.load()));
  }

  openDocument(document: AmendmentDocument): void {
    this.documentService.preview({
      id: document.id,
      tag: document.tag,
      name: document.name,
      mimeType: 'application/pdf',
      createdAt: document.attachedAt,
      size: 0,
    });
  }

  /** Qui, à l'administration du personnel, a repris l'avenant, et quand (RG-avenant-coche-edite). */
  editedLabel(amendment: Amendment): string {
    const at = amendment.adpEditedAt
      ? new DatePipe('fr-FR').transform(amendment.adpEditedAt, 'dd/MM/yyyy à HH:mm')
      : '?';
    return `Édité par ${amendment.adpEditedBy ?? '?'} le ${at}`;
  }

  reasonsOf(amendment: Amendment): string {
    return amendment.reasons.map((reason) => reason.label).join(', ');
  }
}
